"use client";

import { useEffect, useRef } from "react";
import {
  captureClientEvent,
  flushClientTelemetry,
} from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { scheduleLoadErrorRecovery } from "@/lib/load-error-recovery.client";
import { realtimeClient } from "@/lib/realtime/client";
import { queryRenderedSelector } from "@/lib/ui/rendered-elements";

/**
 * Last-resort recovery for a route that never leaves its loading skeleton.
 *
 * ## Why this has to exist
 *
 * With `cacheComponents`, a navigation commits the prefetched static shell
 * first — which is the skeleton — and then fills the dynamic holes from a
 * second request. When that response fails to fulfil a pending segment,
 * Next treats it as a tree mismatch and retries the navigation. From
 * `ppr-navigations.js`:
 *
 *     // Some data failed to finish loading. Trigger a soft retry that
 *     // re-fetches the tree's dynamic data.
 *     // TODO: As an extra precaution against soft retry loops, consider
 *     // tracking whether a navigation was itself triggered by a retry. If two
 *     // happen in a row, fall back to a hard retry.
 *     const isHardRetry = false;
 *
 * That guard does not exist yet, and 16.3.5 is the latest release. A genuine
 * network failure exits with a hard MPA navigation and rescues the user; a
 * *tree mismatch* retries forever with the shell still committed. The result
 * is a skeleton that never clears while the server keeps answering 200 in
 * under half a second — observed in production, along with 476 pageviews of a
 * single URL in one 31-minute session.
 *
 * Nothing inside the app can unstick that state: no error is thrown, so no
 * error boundary fires, and each retry looks like a fresh navigation. Only a
 * document reload escapes. This watchdog is that reload.
 *
 * ## Why it watches the DOM
 *
 * The honest signal for "this route never rendered" is that its skeleton is
 * still on screen. Every `<Skeleton>` carries `data-slot="skeleton"`, so one
 * selector covers all 85 skeleton components. `queryRenderedSelector` skips
 * matches inside hidden `<Activity>` subtrees — without it, the skeleton of a
 * route the user already left would keep this timer running forever.
 *
 * Deliberately generous thresholds: a false positive reloads a page someone is
 * reading, which is worse than waiting another few seconds. A real stuck route
 * never recovers, so the cost of waiting is bounded and the cost of being
 * trigger-happy is not.
 */

// Every duration here is measured with `performance.now()`, never
// `Date.now()`. `Date.now()` follows the system clock, so an NTP correction or
// a sleep/wake mid-measurement produces a duration that is wrong and can be
// negative — and a watchdog that reloads someone's page acts on these numbers.
// The browser's own Resource Timing entries, which `summarizeRouteRequests`
// and `summarizeOriginHealth` read, are on the same monotonic timeline, so the
// two sets of numbers are directly comparable.

/** Poll cadence. One `querySelectorAll` + rect check; negligible. */
const POLL_MS = 2_000;
/**
 * A skeleton only counts as a failed *navigation* if it was already on screen
 * this soon after the URL changed.
 *
 * `data-slot="skeleton"` is on every skeleton in the app, including the inline
 * ones an infinite-scroll list or a lazy tab shows mid-session. Those can
 * legitimately sit on screen for a long time on a bad connection, and reloading
 * the page underneath someone who is merely waiting for the next page of a feed
 * would be a worse bug than the one this guards against. Requiring the skeleton
 * to date from the navigation itself separates "this route never rendered" from
 * "part of this page is loading".
 */
const NAVIGATION_GRACE_MS = 5_000;
/** Report (but don't act) once a skeleton outlives any plausible render. */
const WARN_MS = 3_000;
/**
 * Reload once a skeleton has outlived every plausible render.
 *
 * Was 20s, chosen when a stuck navigation might still have been something that
 * recovered on its own. It is not. The cause is now measured: Next answers a
 * prefetch for these routes with a ZERO-byte body where a navigation gets
 * 85-255 kB, so the router commits a shell it can never fill and soft-retries
 * against the identical tree forever. Nothing about waiting longer changes
 * that outcome — production telemetry has never once shown a stuck navigation
 * resolve itself.
 *
 * So the wait is pure cost, and it is the whole user-visible symptom. A
 * recovery reload of these pages measures 304-611ms. Twenty seconds of
 * skeleton is an app that looks broken; five seconds followed by a
 * half-second reload is a blink. Given roughly 10-22% of pageviews hit this,
 * that difference is most of what users actually experience.
 *
 * Still not instant, deliberately: a genuinely slow dynamic render — a cold
 * function plus a Turso round trip — has been measured at ~3.2s, and reloading
 * that would be a false positive that throws away work in progress.
 */
const RECOVER_MS = 5_000;
/**
 * Backstop for a skeleton that does NOT look navigation-shaped.
 *
 * Long enough that an infinite-scroll list fetching its next page on a bad
 * connection has finished many times over, so a healthy page is never reloaded
 * under someone — but finite, so no one is ever stranded permanently because a
 * single timing heuristic disagreed with them.
 */
const HARD_RECOVER_MS = 45_000;
/**
 * Recoveries allowed per browser session before we stop trying.
 *
 * Was 2, which assumed a stuck navigation was rare enough that needing a third
 * one meant the watchdog itself was misfiring. At 100-222 stuck navigations
 * per 1,000 pageviews, a normal session hits this several times, and the
 * budget ran out mid-session — stranding someone permanently on the very
 * failure this exists to clear.
 *
 * The reload loop this cap guards against cannot happen silently: every
 * recovery emits `navigation_stuck_recovered`, so a runaway is visible in
 * telemetry within minutes rather than inferred from a stuck user.
 */
const MAX_RECOVERIES_PER_SESSION = 6;

/**
 * How long a probe waits before giving up.
 *
 * Was 8s, which was too generous in the one situation that matters. These
 * probes fire precisely when the origin is already struggling, and three
 * requests that sit open for eight seconds each are three more streams held
 * on a connection that has none to spare — the observer making the thing it
 * observes worse, and leaving `(canceled) 8.00 s` rows all over the network
 * panel.
 *
 * A healthy origin answers all three in well under 300ms (measured: 28-307ms
 * for the control probe across every captured event). Two seconds is still
 * roughly seven times the observed healthy latency, so a timeout here remains
 * real evidence of a stall rather than ordinary slowness.
 */
const PROBE_TIMEOUT_MS = 2_000;

const SKELETON_SELECTOR = '[data-slot="skeleton"]';
const ATTEMPTS_STORAGE_KEY = "hiive:navigation-watchdog-attempts";

function readAttempts(): number {
  try {
    const raw = window.sessionStorage.getItem(ATTEMPTS_STORAGE_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    // Private modes throw on sessionStorage. Treat as a first attempt: the
    // reload guard inside scheduleLoadErrorRecovery still holds in memory.
    return 0;
  }
}

function writeAttempts(next: number): void {
  try {
    window.sessionStorage.setItem(ATTEMPTS_STORAGE_KEY, String(next));
  } catch {
    // Best effort.
  }
}

/**
 * Ask the server about the page we are stuck on, and report what it says.
 *
 * The stuck state itself lives in the client router, but its *trigger* does
 * not: Next only enters this retry when the tree it receives disagrees with
 * the tree that was prefetched, and its own source attributes that to "some
 * runtime routing condition changed, likely in a proxy". The app never fails
 * this way when served by a single local process — only behind the production
 * edge — so the difference is in what that front returns.
 *
 * Three things distinguish the plausible causes, and none of them can be seen
 * from outside a real failure:
 *  - `x-nextjs-deployment-id` vs the build this client is running → version
 *    skew, a client holding one deployment's graph while the front answers
 *    from another.
 *  - `age` (a cached prerendered shell served long after the render the
 *    client is trying to reconcile it with).
 *  - `x-matched-path` → a routing decision that differs from the one the
 *    prefetch was built against.
 *
 * Deliberately a plain `fetch` with `RSC: 1` rather than a router call: this
 * must observe the failure, never perturb it.
 */
async function probeStuckRoute(url: string) {
  const started = performance.now();
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { RSC: "1" },
      cache: "no-store",
      credentials: "same-origin",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    // Split deliberately. `await fetch(...)` resolves on HEADERS; the body is
    // a stream that may still be open. A flight response whose headers arrive
    // in 150ms and whose body never ends is indistinguishable from a healthy
    // one until these two are measured apart — and that shape (fast 200, no
    // content) is exactly what a page stuck on a skeleton would produce.
    const headersMs = performance.now() - started;
    const header = {
      probe_status: response.status,
      probe_headers_ms: Math.round(headersMs),
      probe_content_length: response.headers.get("content-length"),
      probe_deployment_id: response.headers.get("x-nextjs-deployment-id"),
      probe_age: response.headers.get("age"),
      probe_matched_path: response.headers.get("x-matched-path"),
      probe_is_prerender: response.headers.get("x-nextjs-prerender"),
      probe_postponed: response.headers.get("x-nextjs-postponed"),
      // The cache key, in effect. A cached RSC response that does not vary on
      // `Next-Router-Prefetch` lets a PREFETCH response — the static shell
      // alone, which is the skeleton — be served to a NAVIGATION that needed
      // the dynamic payload. That is a 200, it is fast, and it is useless:
      // exactly the shape production shows.
      probe_vary: response.headers.get("vary"),
    };

    try {
      const body = await response.text();
      return {
        ...header,
        probe_body_ms: Math.round(performance.now() - started - headersMs),
        probe_bytes: body.length,
        // The first rows of a flight stream are structural — the build id, the
        // tree shape — not page copy. Enough to tell a real tree from a stub,
        // short enough not to ship someone's content into an analytics event.
        probe_head: body.slice(0, 120),
        // A server error serialised INTO the stream: the response is a 200,
        // the body is an error row, and nothing ever renders.
        probe_has_error_row: /"digest"|\bE\d*:/.test(body),
      };
    } catch (bodyError) {
      // Headers arrived, body did not. The single most diagnostic outcome
      // this function can produce.
      return {
        ...header,
        probe_body_ms: Math.round(performance.now() - started - headersMs),
        probe_body_error:
          bodyError instanceof Error ? bodyError.name : "unknown",
      };
    }
  } catch (error) {
    return {
      probe_status: -1,
      probe_headers_ms: Math.round(performance.now() - started),
      probe_error: error instanceof Error ? error.name : "unknown",
    };
  }
}

/**
 * The control in the experiment.
 *
 * The route probe established that a stuck page cannot fetch its own URL — it
 * times out after 8s while `navigator.onLine` is true. That leaves two very
 * different explanations, and they need opposite fixes:
 *
 *  - the SERVER is hanging on this route, or
 *  - the browser's HTTP/2 connection to the origin has wedged, so nothing on
 *    it completes regardless of route.
 *
 * `/manifest.webmanifest` is the cheapest same-origin request that exists —
 * a small, cacheable, dynamic-work-free response. Run it alongside the route
 * probe and the answer falls out: if this succeeds while the route times out,
 * the connection is healthy and the route is at fault. If both time out, the
 * route is innocent and the connection is the problem.
 *
 * Cache-busted so it cannot be answered from disk cache, which would make a
 * wedged connection look healthy.
 */
async function probeOriginControl() {
  const started = performance.now();
  try {
    const response = await fetch(`/manifest.webmanifest?probe=${started}`, {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return {
      control_status: response.status,
      control_ms: Math.round(performance.now() - started),
    };
  } catch (error) {
    return {
      control_status: -1,
      control_ms: Math.round(performance.now() - started),
      control_error: error instanceof Error ? error.name : "unknown",
    };
  }
}

/**
 * What the ROUTER actually did, read back from the browser's own records.
 *
 * Every probe above is synthetic: it asks a fresh question and gets a fresh
 * answer, which is why it keeps coming back healthy on a page that is plainly
 * not. Resource Timing holds the real thing — every request the router made
 * for this URL, with its real duration, its real size, its real status, and
 * whether it needed a brand-new connection.
 *
 * That last one is the point. Two theories survive, and real traffic
 * separates them where a synthetic request cannot:
 *
 *  - A RETRY LOOP. Next 16 has no guard against soft-retry recursion (see the
 *    TODO quoted at the top). Its signature is many requests to one URL, each
 *    fast, each tiny, none of them ever satisfying the tree. Production shows
 *    the same image re-fetched seven times in a row while a skeleton sits on
 *    screen — that is a component remounting, which is that loop.
 *  - A WEDGED CONNECTION. Everything to this origin multiplexes over one
 *    HTTP/2 connection. Its signature is the opposite: few requests, long
 *    durations, and a `connect` phase on requests that should have reused a
 *    warm connection, because Chrome gave up and dialled again.
 *
 * `count` and `newConnections` answer that directly, from what happened rather
 * than from what happens when we ask.
 */
function summarizeRouteRequests(url: string) {
  try {
    const path = new URL(url, window.location.href).pathname;
    const entries = performance.getEntriesByType(
      "resource",
    ) as PerformanceResourceTiming[];

    const mine = entries.filter((entry) => {
      try {
        const parsed = new URL(entry.name);
        return (
          parsed.origin === window.location.origin && parsed.pathname === path
        );
      } catch {
        return false;
      }
    });

    if (mine.length === 0) return { route_requests: 0 };

    const recent = mine.slice(-25);
    const durations = recent.map((entry) => Math.round(entry.duration));
    // A non-zero connect phase means this request did NOT reuse the origin's
    // warm connection — the browser opened a new one, which it only does when
    // the existing connection is unusable.
    const newConnections = recent.filter(
      (entry) => entry.connectEnd > entry.connectStart,
    ).length;

    return {
      route_requests: mine.length,
      route_new_connections: newConnections,
      route_max_ms: Math.max(...durations),
      route_last_ms: durations.slice(-5),
      route_last_bytes: recent
        .slice(-5)
        .map((entry) => entry.encodedBodySize || entry.transferSize || 0),
      route_last_status: recent
        .slice(-5)
        .map(
          (entry) =>
            (entry as PerformanceResourceTiming & { responseStatus?: number })
              .responseStatus ?? 0,
        ),
      route_protocol: recent[recent.length - 1]?.nextHopProtocol || null,
    };
  } catch {
    return {};
  }
}

/**
 * The health of the shared connection, across every request on it.
 *
 * Telemetry, images, the manifest and the realtime socket all ride the same
 * origin. Production has shown a page hold a skeleton while an analytics
 * POST took 20.4s and a WebSocket handshake took 11.9s on that connection —
 * so "is this route slow" and "is this ORIGIN slow" are different questions,
 * and only the second one explains a page that fails without a failing
 * request.
 */
function summarizeOriginHealth() {
  try {
    const entries = performance.getEntriesByType(
      "resource",
    ) as PerformanceResourceTiming[];
    const since = performance.now() - 60_000;
    const recent = entries.filter(
      (entry) =>
        entry.startTime >= since &&
        entry.name.startsWith(window.location.origin),
    );
    if (recent.length === 0) return { origin_requests_60s: 0 };

    const slow = recent.filter((entry) => entry.duration >= 5_000);
    return {
      origin_requests_60s: recent.length,
      origin_slow_60s: slow.length,
      origin_max_ms: Math.round(
        Math.max(...recent.map((entry) => entry.duration)),
      ),
      origin_new_connections_60s: recent.filter(
        (entry) => entry.connectEnd > entry.connectStart,
      ).length,
      origin_slow_names: slow
        .slice(-5)
        .map((entry) => new URL(entry.name).pathname),
    };
  } catch {
    return {};
  }
}

/**
 * Does this URL answer a prefetch and a navigation with the SAME body?
 *
 * Next serves two different things from one URL. A prefetch
 * (`Next-Router-Prefetch: 1`) gets the static shell — the skeleton, and
 * nothing else. A navigation gets the dynamic payload that fills it. They are
 * supposed to be distinguished by the cache key; `Vary` is what carries that,
 * and `probe_vary` above records whether it does.
 *
 * Production shows one URL answering with 0.8 kB under one `_rsc` token and
 * 3.2 kB under another, and the stuck page's own requests coming back at 0.2,
 * 0.6 and 0.3 kB — all 200, all under 200ms. A response that small cannot
 * fill a page. If a cached prefetch is being served to navigations, every
 * retry re-reads the same useless entry and the skeleton can never clear,
 * which is the loop we keep observing.
 *
 * So ask both questions of the same URL at the same moment. Similar sizes
 * mean the two are indistinguishable at the edge — the cache is answering
 * navigations with the shell. Sizes far apart mean the edge is fine and the
 * fault is in the client router.
 */
async function probePrefetchVariant(url: string) {
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { RSC: "1", "Next-Router-Prefetch": "1" },
      cache: "no-store",
      credentials: "same-origin",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    const body = await response.text();
    return {
      prefetch_status: response.status,
      prefetch_bytes: body.length,
      prefetch_age: response.headers.get("age"),
    };
  } catch (error) {
    return {
      prefetch_status: -1,
      prefetch_error: error instanceof Error ? error.name : "unknown",
    };
  }
}

/**
 * How many requests are IN FLIGHT right now — the number the rest of this file
 * cannot see.
 *
 * Every other measurement here reads Resource Timing, and a Resource Timing
 * entry is only created when a request COMPLETES. That makes the existing
 * `origin_requests_60s` actively misleading during the failure it was built to
 * describe: a connection with four hundred requests stalled on it reports a
 * LOW number, because almost nothing is finishing. One captured event read
 * `origin_requests_60s: 50` with `origin_max_ms: 26` while both probes timed
 * out — which reads as "the origin was idle and fast, yet everything hung" and
 * is really "nothing completed in that window, so there was nothing to count".
 *
 * Saturation is the leading explanation for the whole-connection stalls, and
 * it stands or falls on this number. Under HTTP/2 every request to an origin
 * shares one connection with a server-advertised concurrent-stream limit
 * (Envoy, which fronts the previous host, defaults to 100). Past that ceiling
 * the browser * queues, and a queued request shows as `Stalled` — which is what a 20-second
 * wait that ends with everything completing at once looks like.
 *
 * So: count starts and finishes, and report the peak. A stall reporting a peak
 * in the hundreds confirms saturation. Single digits kills it, and the cause
 * is below the browser.
 *
 * Wraps `fetch` only, so it sees RSC and telemetry traffic but not images or
 * documents. That is the traffic that storms, so it is the traffic that
 * matters — and it keeps this off the browser's own loading paths.
 */
const requestLoad = { inFlight: 0, peak: 0, started: 0 };

function trackRequestLoad(): void {
  try {
    const original = window.fetch;
    // Idempotent: React runs effects twice in development, and a second
    // wrapper would double-count every request.
    if ((original as { __hiiveTracked?: boolean }).__hiiveTracked) return;

    // Cast rather than annotate: `typeof fetch` now carries Bun's static
    // `preconnect` (from @types/bun, pulled in by the socket server), and a
    // wrapper is not obliged to reimplement statics it never forwards.
    const wrapped = ((...args: Parameters<typeof fetch>) => {
      requestLoad.inFlight += 1;
      requestLoad.started += 1;
      if (requestLoad.inFlight > requestLoad.peak) {
        requestLoad.peak = requestLoad.inFlight;
      }
      const settle = () => {
        requestLoad.inFlight -= 1;
      };
      const promise = original(...args);
      // `.then(settle, settle)` on a COPY of the chain, returning the original
      // promise: this must not swallow a rejection, and must not convert an
      // unhandled rejection into a handled one for the real caller.
      promise.then(settle, settle);
      return promise;
    }) as typeof fetch;
    (wrapped as { __hiiveTracked?: boolean }).__hiiveTracked = true;
    window.fetch = wrapped;
  } catch {
    // If the environment refuses the patch every other measurement still
    // works; only the in-flight numbers go missing.
  }
}

/**
 * Is the NETWORK broken, or is the app host?
 *
 * Every probe so far targets the app host. `app.hiive.club` and `buzz.hiive.club`
 * are different hostnames on different IPs, so they get different HTTP/2
 * connections — but they resolve into the same Anycast network and are reached
 * over the same path from the same machine. Production keeps freezing BOTH at
 * once: one capture had the venue page pending indefinitely while `/e/` took
 * 19.8s, `/logs` 10.6s and `/metrics` 3.8s, all finishing within a few seconds
 * of the same moment.
 *
 * Nothing in the app explains that. `in_flight_peak` came back as 4, so it is
 * not this client saturating a connection. Two independent connections
 * stalling and releasing together is either the path to the host or the machine's
 * own network — and those need completely different responses: one is a hosting
 * problem, the other is not an app bug at all.
 *
 * `media.hiive.club` separates them. It is the app's own media host on
 * Cloudflare R2 — different provider, different network, different TLS
 * termination — and it is first-party, so nothing is sent anywhere new.
 *
 *   media responds, app stalls      ->  the machine's network is fine; the path
 *                                       to the host is the problem
 *   both stall                      ->  the local network dropped, and no amount
 *                                       of work on the app will change it
 *
 * `mode: "no-cors"` because a readable response is not needed — only whether
 * the request completed, and how fast.
 */
async function probeIndependentPath() {
  const origin = process.env.NEXT_PUBLIC_R2_PUBLIC_BASE_URL;
  if (!origin) return {};
  const started = performance.now();
  try {
    await fetch(`${origin}/?probe=${Date.now()}`, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return {
      offnet_ok: true,
      offnet_ms: Math.round(performance.now() - started),
    };
  } catch (error) {
    return {
      offnet_ok: false,
      offnet_ms: Math.round(performance.now() - started),
      offnet_error: error instanceof Error ? error.name : "unknown",
    };
  }
}

export function NavigationWatchdog() {
  const stuckSinceRef = useRef<number | null>(null);
  const warnedRef = useRef(false);
  const urlRef = useRef<string | null>(null);
  const urlChangedAtRef = useRef(0);
  /** Set once a reload is scheduled; this document is done deciding. */
  const recoveredRef = useRef(false);

  useEffect(() => {
    // Not in development: a cold Turbopack compile routinely outlives the
    // recovery threshold, and reloading mid-compile would look exactly like
    // the bug this exists to fix.
    if (process.env.NODE_ENV !== "production") return;

    stuckSinceRef.current = null;
    warnedRef.current = false;
    urlRef.current = null;
    recoveredRef.current = false;

    // The Resource Timing buffer defaults to 250 entries and drops the OLDEST
    // once full — which on a page re-fetching the same image seven times is
    // precisely the history worth keeping. Raising it costs a few hundred
    // small objects and is what makes `summarizeRouteRequests` able to count
    // a retry loop rather than its own tail.
    try {
      performance.setResourceTimingBufferSize(600);
    } catch {
      // Not implemented everywhere; the summaries degrade, nothing breaks.
    }

    trackRequestLoad();

    const tick = () => {
      if (recoveredRef.current) return;
      // The URL is read from the document rather than `usePathname` /
      // `useSearchParams`: those would pull this component into the router's
      // render path (and `useSearchParams` needs its own Suspense boundary
      // under cacheComponents), while `location.href` also catches the shallow
      // `pushState` URL changes the app makes directly. Arriving somewhere new
      // restarts the clock, so a slow page never inherits elapsed time from
      // the page before it.
      const urlKey = window.location.href;
      if (urlRef.current !== urlKey) {
        urlRef.current = urlKey;
        urlChangedAtRef.current = performance.now();
        stuckSinceRef.current = null;
        warnedRef.current = false;
      }

      // Visibility gates the RELOAD, not the measurement.
      //
      // Resetting the clock whenever the document reported itself hidden made
      // this whole watchdog dead code wherever `visibilityState` is not a
      // faithful signal — an embedded WebView, a PWA, a browser pane — because
      // the timer could never accumulate past one tick. Measured live: a
      // fronted, plainly visible tab still reported "hidden".
      //
      // Keep timing regardless, and only decline to act while hidden. A page
      // the user cannot see is not one to reload out from under them, but it
      // is still one worth recording.
      const isVisible = document.visibilityState === "visible";

      const skeleton = queryRenderedSelector(SKELETON_SELECTOR);
      if (!skeleton) {
        stuckSinceRef.current = null;
        warnedRef.current = false;
        return;
      }

      const now = performance.now();
      if (stuckSinceRef.current === null) {
        stuckSinceRef.current = now;
        return;
      }

      // A skeleton dating from the navigation itself is evidence the route
      // never rendered. One that appeared later usually belongs to a section
      // of an otherwise-working page — an infinite-scroll list, a lazy tab —
      // see NAVIGATION_GRACE_MS.
      const cameFromNavigation =
        stuckSinceRef.current - urlChangedAtRef.current <= NAVIGATION_GRACE_MS;

      const elapsedMs = now - stuckSinceRef.current;

      // Report FIRST, and report unconditionally.
      //
      // The previous order returned before this when the skeleton did not look
      // navigation-shaped, so the one case we most needed to see — a user
      // staring at a skeleton while the watchdog quietly declined to act —
      // produced no telemetry at all. Both flags ride along so the decision
      // itself is auditable, not just the outcome.
      if (elapsedMs >= WARN_MS && !warnedRef.current) {
        warnedRef.current = true;
        // Read the browser's records BEFORE probing. The probe issues a
        // request to this very URL, which would otherwise land in the counts
        // below and make the router look like it retried one more time than
        // it did.
        const routeSummary = summarizeRouteRequests(urlKey);
        const originSummary = summarizeOriginHealth();
        const realtime = realtimeClient.diagnostics;

        // Fire-and-forget: the probe must never delay or alter the recovery
        // decision below, only describe the conditions it happened under.
        // Both probes run TOGETHER, not in sequence: they have to sample the
        // same moment, or a connection that recovers between them makes the
        // comparison meaningless.
        void Promise.all([
          probeStuckRoute(urlKey),
          probeOriginControl(),
          probePrefetchVariant(urlKey),
          probeIndependentPath(),
        ]).then(([probe, control, prefetch, offnet]) => {
          captureClientEvent(ANALYTICS_EVENTS.NAVIGATION_STUCK_DETECTED, {
            url: urlKey,
            elapsed_ms: Math.round(elapsedMs),
            online: navigator.onLine,
            came_from_navigation: cameFromNavigation,
            visible: isVisible,
            // The build THIS client is running, to compare against the
            // deployment the edge answers from.
            client_commit_sha: process.env.NEXT_PUBLIC_GIT_COMMIT_SHA,
            ws_status: realtime.status,
            ws_ready_state: realtime.readyState,
            ws_churn_count: realtime.churnCount,
            ws_backoff_ms: realtime.backoffMs,
            ws_paused: realtime.paused,
            // The saturation test. Resource Timing counts
            // completions; this counts concurrency.
            in_flight_now: requestLoad.inFlight,
            in_flight_peak: requestLoad.peak,
            fetches_this_document: requestLoad.started,
            ...routeSummary,
            ...originSummary,
            ...probe,
            ...control,
            ...prefetch,
            ...offnet,
          });
        });
      }

      // Never reload a page the user is not looking at.
      if (!isVisible) return;

      // A skeleton that outlives the backstop is stuck whatever its origin.
      // Holding the navigation-shaped requirement absolutely means a single
      // mis-timed sample — a slow navigation, a URL rewritten a beat late —
      // leaves someone stranded forever, which is the failure this exists to
      // prevent. Past this point the balance of harm flips.
      const threshold = cameFromNavigation ? RECOVER_MS : HARD_RECOVER_MS;
      if (elapsedMs < threshold) return;

      const attempts = readAttempts();
      if (attempts >= MAX_RECOVERIES_PER_SESSION) {
        // Out of budget. Stop polling rather than burn CPU on a page we have
        // already decided not to reload again.
        stuckSinceRef.current = null;
        return;
      }

      // A distinct signature per attempt, because the shared reload guard in
      // load-error-recovery keys on it and would otherwise allow only one.
      const error = Object.assign(
        new Error(
          `Navigation stuck on loading skeleton (attempt ${attempts + 1})`,
        ),
        {
          name: "NavigationStuckError",
          digest: `navigation-stuck-${attempts + 1}`,
        },
      );

      const recovering = scheduleLoadErrorRecovery(
        error,
        "transient-load",
        () => {
          captureClientEvent(ANALYTICS_EVENTS.NAVIGATION_STUCK_RECOVERED, {
            url: urlKey,
            elapsed_ms: Math.round(elapsedMs),
            attempt: attempts + 1,
          });
          // Then get it OUT before the reload discards the document.
          //
          // posthog-js batches, and the batch is delivered by an ordinary
          // request — which during the failure being reported is itself stuck:
          // one captured freeze had `/e/` take 19.8s and `/logs` 10.6s while
          // the page waited. The detect event is queued at 3s and the reload
          // lands at 5s, so a batch has two seconds to leave a connection on
          // which nothing is moving. It does not, and the events we most need
          // are the ones that never arrive — a freeze that produced a stuck
          // page produced no telemetry at all.
          //
          // `flushClientTelemetry` sends via `sendBeacon`, which the browser
          // completes independently of the document, so it survives both the
          // stall and the reload.
          flushClientTelemetry();
        },
        // A plain reload, with the cache left alone.
        //
        // This is borrowed machinery: `scheduleLoadErrorRecovery` exists for
        // stale-asset errors, where dropping Cache Storage and the service
        // worker is the actual cure. What we have here is not that. `sw.ts`
        // leaves navigations on NetworkOnly and does not match RSC requests at
        // all, so the service worker is provably uninvolved in a stuck
        // navigation — the stuck state lives in the router, and only the
        // reload clears it.
        //
        // Clearing anyway would cost real ground: the only same-origin cache
        // is `/_next/static/`, whose eviction is the exact ChunkLoadError bug
        // the raised `maxEntries` in `sw.ts` was meant to stop. Wiping it on a
        // heuristic — which can fire on a false positive — would hand that bug
        // back on the next few navigations.
        //
        // What we still want from this function is its reload guard, which is
        // shared with the error boundaries and is what stops two observers
        // racing into a reload loop.
        { clearAppState: false },
      );

      if (recovering) {
        writeAttempts(attempts + 1);
        stuckSinceRef.current = null;
        // A reload is already scheduled, so this document has nothing left to
        // decide. Without this the backstop can fire a second time on the same
        // page while the reload is still settling, spending two of the
        // session's recovery budget on one stuck navigation.
        recoveredRef.current = true;
      }
    };

    const timer = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(timer);
  }, []);

  return null;
}
